/**
 * Remote logger for debugging
 *
 * Sends logs to the Rails backend for debugging in Cucumber tests
 * where React console logs are not easily captured.
 *
 * Always active for debugging purposes.
 */

/**
 * Send a log message to the Rails backend
 * Fire-and-forget - doesn't wait for response or handle errors
 */
function sendLog(level, message, data) {
  // Skip the network call under vitest. The XHR can't reach a backend in
  // jsdom and the failed connect attempts queue extra microtasks that
  // delay subsequent React state flushes — enough to flake waitFor() on
  // slow CI runners (observed in WisdomGame 14-word init). Production
  // and dev (Cucumber) still log normally.
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test') {
    return
  }

  // Use XMLHttpRequest for simplicity and to avoid import issues
  const xhr = new XMLHttpRequest()
  xhr.open('POST', '/api/log', true)
  xhr.setRequestHeader('Content-Type', 'application/json')

  // Fire and forget - don't wait for response
  xhr.send(JSON.stringify({ level, message, data }))
}

/**
 * Remote logger that sends logs to Rails backend
 * Also logs to console for local debugging
 */
export const remoteLog = {
  debug(message, data) {
    console.log(`[DEBUG] ${message}`, data ?? '')
    sendLog('debug', message, data)
  },

  info(message, data) {
    console.log(`[INFO] ${message}`, data ?? '')
    sendLog('info', message, data)
  },

  warn(message, data) {
    console.warn(`[WARN] ${message}`, data ?? '')
    sendLog('warn', message, data)
  },

  error(message, data) {
    console.error(`[ERROR] ${message}`, data ?? '')
    sendLog('error', message, data)
  },

  /**
   * Log with a custom prefix for easy filtering
   */
  log(prefix, message, data) {
    console.log(`[${prefix}] ${message}`, data ?? '')
    sendLog('info', `[${prefix}] ${message}`, data)
  },
}

export default remoteLog
